import {
  valuationMethodOptions,
  type ValuationMethodOption,
} from '@/constants/entities'
import type { ValuationMethodType } from '@/types/services/global'
import type { Entity } from '@/types/services/root'
import type {
  RootUser,
  RootUserListItem,
  RootUserListRelation,
  RootUserRelation,
} from '@/types/services/root_user'
import type { SessionRole } from '@/types/services/user'

export interface RelationsMapByEntityId {
  [key: string]: RootUserRelation | RootUserListRelation
}

type PartialRootUser = Partial<
  Pick<
    RootUser,
    'corporate' | 'first_name' | 'last_name' | 'company_name' | 'email'
  >
>

export function getTextFromRootUser(root_user: PartialRootUser): string {
  if (root_user.corporate) {
    return root_user.company_name || '-'
  }
  if (root_user.first_name == '' && root_user.last_name == '') {
    const email = root_user.email
    return email ? email.split('@')[0] : '-'
  }

  return `${root_user.last_name} ${root_user.first_name}`
}

export function getTextFromEntity(entity: Entity): string {
  if ('root_user' in entity && entity.root_user) {
    return getTextFromRootUser(entity.root_user)
  }
  return entity.name
}

export function isRelationActive(
  relation: RootUserRelation | RootUserListRelation,
  at = new Date()
): boolean {
  const { date_range } = relation
  const from = 'from_' in date_range ? date_range.from_ : date_range.from
  const { to } = date_range

  return (
    (from == null || new Date(from).getTime() <= at.getTime()) &&
    (to == null || new Date(to).getTime() > at.getTime())
  )
}

function filterRelations(
  root_user: RootUserListItem,
  role?: SessionRole,
  entityId?: string
): RootUserListRelation[] {
  return root_user.relations.filter(
    (relation) =>
      (!role || relation.role === role) &&
      (!entityId || relation.entity_id === entityId)
  )
}

export function hasClientRelation(
  rootUser: RootUserListItem,
  entityId: string
): boolean {
  return filterRelations(rootUser, 'client', entityId).length > 0
}

export function hasDeactivatedRelations(
  root_user: RootUserListItem,
  role?: SessionRole,
  entityId?: string,
  at?: Date
): boolean {
  return filterRelations(root_user, role, entityId).every(
    (relation) => !isRelationActive(relation, at)
  )
}

export function hasOnlyLaterRelations(
  root_user: RootUserListItem,
  role: SessionRole,
  entityId: string | undefined,
  at: Date
): boolean {
  const relations = filterRelations(root_user, role, entityId)

  return (
    relations.length > 0 &&
    relations.every(
      ({ date_range }) =>
        date_range.from_ != null &&
        new Date(date_range.from_).getTime() > at.getTime()
    )
  )
}

export function isActiveRootAdmin(rootUser: RootUserListItem): boolean {
  return rootUser.is_superadmin || !hasDeactivatedRelations(rootUser, 'admin')
}

export function getActiveRelationsMapByEntityId(
  relations: (RootUserRelation | RootUserListRelation)[]
) {
  const map: RelationsMapByEntityId = {}

  relations.forEach((relation) => {
    if (isRelationActive(relation)) {
      map[relation.entity_id] = relation
    }
  })

  return map
}

export function getValuationMethodOptionByValue(
  value: ValuationMethodType
): ValuationMethodOption | null {
  return valuationMethodOptions.find((method) => method.value === value) || null
}
